import { type ReactNode } from 'react';
import {
  ResponsiveContainer, LineChart as RLineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { useChartTheme, resolveSeriesColors } from './theme.js';
import { ChartTooltip } from './ChartTooltip.js';
import type { BaseChartProps } from './_internal/shared-types.js';

export interface LineChartProps extends BaseChartProps {
  /** Curve type. */
  curveType?: 'linear' | 'monotone' | 'step' | 'natural';
  /** Render dots on data points. */
  withDots?: boolean;
  /** Stroke width in px. Default 2. */
  strokeWidth?: number;
}

/** Themed line chart. */
export function LineChart(props: LineChartProps): ReactNode {
  const {
    data, dataKey, series,
    h = 280, w = '100%',
    withTooltip = true, withLegend = true,
    withXAxis = true, withYAxis = true, withGrid = true,
    valueFormatter, labelFormatter, tooltipContent,
    curveType = 'monotone', withDots, strokeWidth = 2,
    className
  } = props;
  const theme = useChartTheme();
  const resolved = resolveSeriesColors(series, theme);

  return (
    <div className={['rr-chart', 'rr-chart--line', className].filter(Boolean).join(' ')} style={{ width: w, height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <RLineChart data={data}>
          {withGrid ? <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" /> : null}
          {withXAxis ? <XAxis dataKey={dataKey} stroke={theme.axis} fontSize={11} tickFormatter={labelFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
          {withYAxis ? <YAxis stroke={theme.axis} fontSize={11} tickFormatter={valueFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
          {withTooltip ? <Tooltip cursor={{ stroke: theme.cursor }} content={(tooltipContent ?? <ChartTooltip formatValue={valueFormatter} formatLabel={labelFormatter} />) as never} /> : null}
          {withLegend ? <Legend wrapperStyle={{ fontFamily: 'var(--rr-font-system)', fontSize: 12, color: theme.axisLabel }} /> : null}
          {resolved.map((s) => (
            <Line
              key={s.name}
              type={curveType}
              dataKey={s.name}
              name={s.label ?? s.name}
              stroke={s.color}
              strokeWidth={strokeWidth}
              dot={withDots ? { r: 3, fill: s.color, strokeWidth: 0 } : false}
              activeDot={{ r: 4, fill: s.color }}
              isAnimationActive
            />
          ))}
        </RLineChart>
      </ResponsiveContainer>
    </div>
  );
}
